import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

const ResellerLimitDialog = ({ open, onClose, limitType, navigatePath, navigate }) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();

  // Determine dialog content based on limitType
  const getDialogContent = () => {
    switch (limitType) {
      case 'credit':
        return {
          title: translate('ai_content.credit_limit_exceeded') || 'Credit Limit Reached',
          message: translate('ai_content.credit_limit_exceeded_message') || 'You’ve reached your AI credit limit.',
          action: translate('ai_content.add_credits') || 'Add Credits',
        };
      case 'social':
        return {
          title: translate('ai_content.social_limit_exceeded') || 'Social Accounts Limit Reached',
          message:
            translate('ai_content.social_limit_exceeded_message') ||
            'You’ve reached the maximum number of social accounts.',
          action: translate('ai_content.add_accounts') || 'Add Accounts',
        };
      case 'user':
        return {
          title: translate('ai_content.user_limit_exceeded') || 'User Limit Reached',
          message: translate('ai_content.user_limit_exceeded_message') || 'You’ve reached the maximum number of users.',
          action: translate('ai_content.add_users') || 'Add Users',
        };
      default:
        return {
          title: 'Limit Reached',
          message: 'You’ve reached a limit. Please take action to continue.',
          action: 'Take Action',
        };
    }
  };

  const { title, message, action } = getDialogContent();

  const handleNavigateToBrand = () => {
    navigate(navigatePath);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="limit-dialog-title"
      aria-describedby="limit-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          margin: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <DialogTitle id="limit-dialog-title" sx={{ textAlign: 'center', fontSize: '1.25rem', fontWeight: 600, py: 1.5 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 40, color: theme.palette.error.main }} aria-hidden="true" />
          <Typography
            id="limit-dialog-description"
            variant="body1"
            sx={{ fontSize: '1rem', color: theme.palette.text.primary }}
          >
            {message}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
            {translate('ai_content.please_take_action') || 'Please take action to continue.'}
          </Typography>
        </Box>
        <DialogContent>
          <Typography paragraph>
            {translate('brand.limits_purpose_1') ||
              'In reseller mode you can create multiple brands (sub-accounts / customers) under your agency / reseller account.'}
          </Typography>

          <Typography paragraph>{translate('brand.limits_purpose_2') || 'Setting limits helps you:'}</Typography>

          <ul style={{ paddingLeft: '1.5rem', margin: '8px 0 16px' }}>
            <li>{translate('brand.limits_reason_users') || 'Control how many users each customer can invite'}</li>
            <li>
              {translate('brand.limits_reason_accounts') || 'Restrict number of connected social accounts per brand'}
            </li>
            <li>
              {translate('brand.limits_reason_ai') ||
                'Manage AI usage (text generation & image generation tokens) and prevent unexpected costs'}
            </li>
          </ul>

          <Typography paragraph sx={{ fontWeight: 500, mt: 3 }}>
            {translate('brand.dont_want_limits') || 'Don’t want to set any limits?'}
          </Typography>

          <Typography>
            {translate('brand.disable_reseller_hint') ||
              'You can disable reseller mode at any time. After disabling, you will only manage one main brand without sub-limits.'}
          </Typography>
        </DialogContent>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2, pt: 1, gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNavigateToBrand}
          sx={{ textTransform: 'none', fontSize: '0.875rem', px: 2.5, py: 0.75, borderRadius: 1 }}
        >
          {action}
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={onClose}
          sx={{ textTransform: 'none', fontSize: '0.875rem', px: 2.5, py: 0.75, borderRadius: 1 }}
        >
          {translate('common.cancel') || 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResellerLimitDialog;
